import { Dropdown, Menu } from "antd";
import dayjs from "dayjs";
import { ReactNode, useState } from "react";
import { ReactComponent as CircleRegularIcon } from "../../assets/icons/circle-regular.svg";
import { ReactComponent as ChevronDownRegularIcon } from "../../assets/icons/font-awesome/chevron-down-regular.svg";
import { ReactComponent as CircleDotSolidIcon } from "../../assets/icons/font-awesome/circle-dot-solid.svg";
import { ReactComponent as FilterRegularIcon } from "../../assets/icons/font-awesome/filter-regular.svg";
import { CustomDatePicker } from "../../molecules/chips/date-selector/custom-date-selector";
import { RangeValue } from "../../molecules/chips/date-selector/types";
import { TimeFilterType } from "../../stores/billing-store/types";
import { track } from "../../utils/tracking";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";
import queryClient from "../../instances/react-query-client";
import { getAppointmentsQueryNameV3 } from "../../payment-dashboard-v2/api/useGetAllAppointmentsV2";
import { getAclViewClassName } from "@elixir/utils";
import { SUBJECT_KEYS } from "@elixir/types";

export const whitelistedDoctorOidFor31days = [
  "167457489264308",
  "168049697669917",
  "167516677556762",
  "161419272566611",
  "164009477895950",
  "165070922566035",
  "167974970273415",
  "167524575268761",
  "168378167073403",
  "167655505193851",
  "164562535210805",
  "167421787357638",
  "165302021878446",
  "165987890444010",
  "166386517910037",
  "164733883524158",
  "168198027938472",
  "168217118606324",
  "168120352053385",
  "163013796978932",
  "168767621634922",
  "168873436510210",
  "165943766987173",
  "163489037735977",
  "166082847050946",
  "168188209269081",
  "168226282219076",
  "164767390729527",
  "168473700733146",
  "169804951312473",
  "168302908298957",
];

function TimeFilters({
  onClick,
  selectedTimeline,
  filters = defaultFilters,
  customDatePickerMaxRange = 7,
}: TimeFiltersProps): JSX.Element {
  const { data: myAccessProfile } = useMyAccessProfileV2();
  const [dates, setDates] = useState<RangeValue<dayjs.Dayjs> | undefined>();

  return (
    <div className={`flex items-center space-x-16 ${getAclViewClassName(SUBJECT_KEYS.PAY_DASH_BEYOND_TODAY)}`}>
      <PaymentFilterSelector
        selectedTimeline={selectedTimeline}
        filters={filters}
        onClick={(id: TimeFilterType) => {
          queryClient.removeQueries(getAppointmentsQueryNameV3());
          onClick({ timeFilter: id });
          setDates(undefined);
          let startFromDate = dayjs().startOf("day").toISOString();
          let endDate = dayjs().endOf("day").toISOString();
          if (selectedTimeline === "yesterday") {
            startFromDate = dayjs()
              .subtract(1, "days")
              .startOf("day")
              .toISOString();
            endDate = dayjs().subtract(1, "days").endOf("day").toISOString();
          } else if (selectedTimeline === "last-7-days") {
            startFromDate = dayjs()
              .subtract(7, "days")
              .startOf("day")
              .toISOString();
          }
          track("BILLING_TIME_RANGE_SELECT", {
            type: selectedTimeline,
            startDate: startFromDate,
            endDate,
          });
        }}
      />
      {/* {filters.map(({ id, label }) => (
        <TimeFilterBox
          key={id}
          label={label}
          onClick={}
          isSelected={selectedTimeline === id}
        />
      ))} */}

      <CustomDatePicker
        dates={dates}
        setDates={setDates}
        onDatesSelected={(startDate, endDate) => {
          onClick({
            timeFilter: "custom",
            duration: {
              startDate,
              endDate,
            },
          });
          track("BILLING_TIME_RANGE_SELECT", {
            type: "custom",
            startDate,
            endDate,
          });
        }}
        className={`custom-date-picker BodyBody2Regular dr-rounded-8 ${selectedTimeline === "custom"
          ? "dr-text-darwin-touch-neutral-1000 filled-dates"
          : "dr-border-none"
          }`}
        noOfDays={
          whitelistedDoctorOidFor31days.includes(
            myAccessProfile?.loginmeta?.docid!
          )
            ? 31
            : customDatePickerMaxRange
        }
      />
    </div>
  );
}

const PaymentFilterSelector = ({
  selectedTimeline,
  filters = [],
  onClick,
}: {
  selectedTimeline: TimeFilterType | "custom";
  filters: {
    id: TimeFilterType;
    label: string;
  }[];
  onClick: (id: TimeFilterType) => void;
}) => {
  const menu = (
    <Menu className="dr-rounded-8 dr-overflow-hidden dr-w-200 dr-min-w-200 BodyBold ">
      <div className="dr-px-16 dr-py-8 dr-flex dr-items-center dr-space-x-8 ">
        <FilterRegularIcon />
        <div>Filter</div>
      </div>
      <div className="dr-w-full dr-h-1 dr-bg-darwin-touch-neutral-200"></div>
      {filters.map(({ id, label }) => {
        const isSelected = id === selectedTimeline;
        return (
          <Menu.Item
            id={id}
            onClick={() => onClick(id)}
            className="dr-px-16"
            icon={
              isSelected ? (
                <CircleDotSolidIcon className="dr-w-14 dr-h-14 dr-text-darwin-touch-primary-primary" />
              ) : (
                <CircleRegularIcon className="dr-w-14 dr-h-14" />
              )
            }
          >
            <div
              className={`dr-flex dr-items-center dr-justify-between ${isSelected ? "BodyBold" : "BodyRegular"
                } dr-py-3 dr-text-darwin-touch-neutral-1000`}
            >
              <div>{label}</div>
            </div>
          </Menu.Item>
        );
      })}
    </Menu>
  );
  return (
    <Dropdown trigger={["click"]} overlay={menu} placement="bottomLeft">
      <div className="dr-flex dr-items-center dr-justify-between dr-px-7 dr-text-darwin-touch-neutral-1000 dr-rounded-8 dr-py-6 dr-w-138 dr-bg-darwin-touch-neutral-50 dr-cursor-pointer dr-space-x-8 BodyBold">
        <div className="dr-flex dr-items-center dr-space-x-9">
          <FilterRegularIcon className="dr-w-24 dr-h-24 dr-p-5" />
          <div>
            {filters?.find(({ id }) => id === selectedTimeline)?.label ||
              "Custom"}
          </div>
        </div>
        <div className="dr-w-16 dr-h-16 dr-flex dr-items-center dr-justify-center">
          <ChevronDownRegularIcon className="dr-w-13 dr-h-16" />
        </div>
        {/* <CaretDownRegularIcon className="w-12 dr-text-darwin-touch-neutral-600 dr-cursor-pointer" /> */}
      </div>
    </Dropdown>
  );
};

// function TimeFilterBox({ label, onClick, isSelected }: TimeFiltersBox) {
//   return (
//     <div
//       className={`px-12 py-4 border cursor-pointer rounded-24 text-13 whitespace-nowrap ${
//         isSelected
//           ? "text-white border-transparent bg-actionPrimary"
//           : "text-actionPrimary border-actionPrimary"
//       }`}
//       onClick={onClick}
//     >
//       {label}
//     </div>
//   );
// }

const defaultFilters: { id: TimeFilterType; label: string }[] = [
  {
    id: "today",
    label: "Today",
  },
  {
    id: "yesterday",
    label: "Yesterday",
  },
  {
    id: "last-7-days",
    label: "Last 7 Days",
  },
];

export type TimeFiltersProps = {
  selectedTimeline: TimeFilterType | "custom";
  filters?: { id: TimeFilterType; label: string }[];
  onClick: (props: {
    timeFilter: TimeFilterType | "custom";
    duration?: { startDate: string; endDate: string };
  }) => void;
  customDatePickerMaxRange?: number;
};

interface TimeFiltersBox {
  label: string | ReactNode;
  onClick?: () => void;
  isSelected: boolean;
}

export default TimeFilters;
